import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Chip,
  LinearProgress,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useGame } from '../context/GameProvider';
import type { Quest } from '../types';

interface QuestPanelProps {
  customerId?: string;
  onSelectQuest?: (quest: Quest) => void;
}

function questTypeColor(type: Quest['type']): 'primary' | 'warning' | 'error' | 'success' | 'default' {
  switch (type) {
    case 'boss_battle': return 'error';
    case 'investigation': return 'warning';
    case 'onboarding': return 'primary';
    case 'improvement': return 'success';
    default: return 'default';
  }
}

const QuestPanel: React.FC<QuestPanelProps> = ({ customerId, onSelectQuest }) => {
  const { quests, loading, loadQuests } = useGame();

  useEffect(() => {
    loadQuests(customerId);
  }, [customerId, loadQuests]);

  const activeQuests = quests.filter((q) => q.status === 'active');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <AssignmentIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2">Active Quests</Typography>
        {activeQuests.length > 0 && (
          <Chip label={activeQuests.length} size="small" color="primary" />
        )}
      </Box>

      {loading && (
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!loading && activeQuests.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No active quests.
        </Typography>
      )}

      <List disablePadding dense>
        {activeQuests.map((quest) => (
          <ListItem key={quest.id} disablePadding>
            <ListItemButton
              onClick={() => onSelectQuest?.(quest)}
              sx={{ borderRadius: 1, mb: 0.5, flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <ListItemText
                  primary={quest.title}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium', noWrap: true }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <Chip
                  label={quest.type.replace('_', ' ')}
                  size="small"
                  color={questTypeColor(quest.type)}
                  sx={{ flexShrink: 0 }}
                />
              </Box>
              <Box sx={{ width: '100%', mt: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={quest.completion_percentage}
                  sx={{ height: 4, borderRadius: 1 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                {quest.completion_percentage}% complete
              </Typography>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default QuestPanel;
